import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { authService } from '../lib/auth';
import { getNavigationItems, getUserRoleFromDB, normalizeRole, type UserRole, type NavigationItem } from '../lib/rbac';
import { useStore } from '../lib/store';
import { getTenantSlugFromHostname } from '../lib/tenant';
import toast from 'react-hot-toast';
import {
  Home,
  Users,
  Calendar,
  UserCheck,
  CreditCard,
  BarChart3,
  Settings,
  Menu,
  X,
  Heart,
  LogOut,
  Package,
  Stethoscope,
  FileText,
  DollarSign,
  Microscope,
  Activity,
  ChevronDown
} from 'lucide-react';

const MainLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useStore();
  const [userRole, setUserRole] = useState<UserRole>('receptionist');
  const [loadingRole, setLoadingRole] = useState(true);
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(() => {
    const tenantSlug = getTenantSlugFromHostname() || 'default';
    const storageKey = `sidebar.expanded.v1:${tenantSlug}:${user?.id || 'guest'}`;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    loadUserRole();
  }, [user]);

  const loadUserRole = async () => {
    const metadataRole = user?.user_metadata?.role || user?.app_metadata?.role;
    const fallbackRole = normalizeRole(metadataRole);
    
    if (!user?.id) {
      setUserRole(fallbackRole);
      setLoadingRole(false);
      return;
    }

    try {
      const role = await getUserRoleFromDB(user.id);
      setUserRole(role || fallbackRole);
    } catch (error) {
      console.error('Error loading user role:', error);
      setUserRole(fallbackRole);
    } finally {
      setLoadingRole(false);
    }
  };
  
  const getIconForRoute = (href: string) => {
    const iconMap: Record<string, any> = {
      '/': Home,
      '/patients': Users,
      '/appointments': Calendar,
      '/doctors': UserCheck,
      '/staff': Users,
      '/consultations': Stethoscope,
      '/orders': FileText,
      '/opd': Calendar,
      '/lab': FileText,
      '/radiology': Microscope,
      '/diagnosis': Activity,
      '/referrals': Users,
      '/doctor/prescriptions': FileText,
      '/billing': CreditCard,
      '/reports': BarChart3,
      '/pharmacy': Package,
      '/payroll': DollarSign,
      '/settings/users': Users,
      '/settings': Settings,
    };
    return iconMap[href] || Home;
  };
  
  const baseNavigation: (NavigationItem & { icon: any })[] = loadingRole ? [] : (userRole === 'doctor' ? [
    { name: 'Dashboard', href: '/', permission: 'view_dashboard' as any, icon: getIconForRoute('/') },
    { name: 'Patients', href: '/patients', permission: 'view_patients_limited' as any, icon: getIconForRoute('/patients') },
    { name: 'Appointments', href: '/appointments', permission: 'view_appointments' as any, icon: getIconForRoute('/appointments') },
    { name: 'Consultations', href: '/consultations', permission: 'view_consultations' as any, icon: getIconForRoute('/consultations') },
    { name: 'Orders', href: '/orders', permission: 'view_orders' as any, icon: getIconForRoute('/orders') },
    { name: 'Referrals', href: '/referrals', permission: 'view_referrals' as any, icon: getIconForRoute('/referrals') },
    { name: 'Prescriptions', href: '/doctor/prescriptions', permission: 'manage_own_prescriptions' as any, icon: getIconForRoute('/doctor/prescriptions') },
    { name: 'Reports', href: '/reports', permission: 'view_own_reports' as any, icon: getIconForRoute('/reports') },
    { name: 'Settings', href: '/settings', permission: 'view_settings' as any, icon: getIconForRoute('/settings') },
  ] : getNavigationItems(userRole).map(item => ({
    ...item,
    icon: getIconForRoute(item.href)
  })));

  const minimalFallbackNav: (NavigationItem & { icon: any })[] = [
    { name: 'Dashboard', href: '/', permission: 'view_dashboard' as any, icon: Home },
    { name: 'Patients', href: '/patients', permission: 'view_patients' as any, icon: Users },
    { name: 'Appointments', href: '/appointments', permission: 'view_appointments' as any, icon: Calendar },
    { name: 'Settings', href: '/settings', permission: 'view_settings' as any, icon: Settings },
  ];

  const navigation = (!loadingRole && baseNavigation.length === 0) ? minimalFallbackNav : baseNavigation;

  const findActiveParentKey = (pathname: string, navItems: typeof navigation): string | null => {
    for (const item of navItems) {
      const key = item.href || item.name;
      if (item.children && item.children.length > 0) {
        if (item.children.some(child => pathname.startsWith(child.href))) {
          return key;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (navigation.length === 0) return;
    
    const activeParentKey = findActiveParentKey(location.pathname, navigation);
    if (activeParentKey && !expandedMenus.has(activeParentKey)) {
      setExpandedMenus(prev => {
        const next = new Set(prev);
        next.add(activeParentKey);
        return next;
      });
    }
  }, [location.pathname, navigation]);

  useEffect(() => {
    const tenantSlug = getTenantSlugFromHostname() || 'default';
    const storageKey = `sidebar.expanded.v1:${tenantSlug}:${user?.id || 'guest'}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(expandedMenus)));
    } catch (error) {
      console.error('Failed to save sidebar state:', error);
    }
  }, [expandedMenus, user?.id]);

  const toggleMenu = (key: string) => {
    setExpandedMenus(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
      toast.success('Logged out successfully');
    } catch (error: any) {
      toast.error(error.message || 'Logout failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-900 bg-opacity-50 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-teal-800 to-teal-900 shadow-soft-lg border-r border-teal-700 overflow-y-auto [scrollbar-gutter:stable] transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-teal-700 bg-teal-800">
          <div className="flex items-center space-x-2">
            <Heart className="w-8 h-8 text-teal-200" />
            <span className="text-xl font-bold text-white">MedSystem</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md hover:bg-teal-700 transition-colors"
          >
            <X className="w-6 h-6 text-teal-200" />
          </button>
        </div>

        <nav className="mt-6 px-3">
          <ul className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const key = item.href || item.name;
              const isExpanded = expandedMenus.has(key);
              
              if (item.children && item.children.length > 0) {
                return (
                  <li key={item.name} className="space-y-1">
                    <button
                      type="button"
                      onClick={() => toggleMenu(key)}
                      aria-expanded={isExpanded}
                      aria-controls={`section-${key}`}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-teal-100 rounded-lg hover:bg-teal-700 transition-all duration-200"
                    >
                      <span className="flex items-center">
                        <Icon className="w-5 h-5 mr-3 text-teal-300" />
                        {item.name}
                      </span>
                      <ChevronDown 
                        className={`w-4 h-4 text-teal-300 transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <div 
                      id={`section-${key}`}
                      className={`overflow-hidden transition-all duration-200 ${
                        isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <ul className="ml-8 space-y-1 py-1">
                        {item.children.map((child) => (
                          <li key={child.name}>
                            <NavLink
                              to={child.href}
                              className={({ isActive }) =>
                                `flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                                  isActive
                                    ? 'bg-teal-700 text-white font-medium shadow-sm'
                                    : 'text-teal-200 hover:bg-teal-700 hover:text-white'
                                }`
                              }
                              onClick={() => setSidebarOpen(false)}
                            >
                              {child.name}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                );
              }
              
              return (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-teal-700 text-white border-r-2 border-teal-400 shadow-sm'
                          : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                      }`
                    }
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Main content area */}
      <div className="flex-1 min-w-0 flex flex-col lg:ml-64">
        
        {/* Top bar */}
        <div className="fixed top-0 left-0 lg:left-64 right-0 h-14 z-10 bg-white border-b border-neutral-200 px-4 sm:px-6 lg:px-8 flex items-center shadow-sm">
          <div className="flex items-center justify-between w-full">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-neutral-600 font-medium">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-neutral-700 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-none px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
